import React, { useState, useEffect, useRef } from "react";

const API_URL = process.env.REACT_APP_API_URL;
const SLIDE_INTERVAL_MS = 3000; // 3s auto-advance between slides
const SLIDE_TRANSITION_MS = 600;

/**
 * Social Feed — endless auto-scrolling carousel of Instagram post embeds,
 * with the URLs curated by the site owner through the admin CRUD screen.
 *
 * How the loop works:
 *   - Posts come from our own backend (/api/instagram-posts).
 *   - Each post URL is converted to its /embed variant on render, so IG
 *     renders the canonical embed (no third-party widget dependency).
 *   - The visible count (`perView`) is responsive: 3 on desktop, 2 on
 *     tablet, 1 on phone.
 *   - For a seamless infinite loop the track duplicates the first `perView`
 *     posts at the end. When the index lands on the cloned segment, we
 *     briefly disable the CSS transition and snap the index back to 0 so
 *     the user never sees the rewind.
 *   - Auto-advance pauses on mouse hover / touch so users can interact
 *     with an embed without it sliding out from under them.
 */
function toEmbedUrl(raw) {
  let u = (raw || "").trim();
  if (!u) return "";
  // Strip query/hash.
  u = u.split("?")[0].split("#")[0];
  // Normalize trailing slash.
  if (!u.endsWith("/")) u += "/";
  if (u.endsWith("/embed/")) return u;
  return u + "embed/";
}

function computePerView(width) {
  if (width < 640) return 1;
  if (width < 960) return 2;
  return 3;
}

export default function SocialFeed() {
  const [posts, setPosts] = useState([]);
  const [perView, setPerView] = useState(() =>
    typeof window !== "undefined" ? computePerView(window.innerWidth) : 3,
  );
  const [index, setIndex] = useState(0);
  const [animating, setAnimating] = useState(true);
  const [paused, setPaused] = useState(false);
  const timerRef = useRef(null);

  // Fetch curated posts once.
  useEffect(() => {
    fetch(`${API_URL}/api/instagram-posts`)
      .then((r) => r.json())
      .then((data) => setPosts(Array.isArray(data) ? data : []))
      .catch((err) => console.error("Failed to load Instagram posts:", err));
  }, []);

  // Keep perView in sync with viewport.
  useEffect(() => {
    const update = () => setPerView(computePerView(window.innerWidth));
    window.addEventListener("resize", update);
    window.addEventListener("orientationchange", update);
    return () => {
      window.removeEventListener("resize", update);
      window.removeEventListener("orientationchange", update);
    };
  }, []);

  // Auto-advance. Skip if there aren't more posts than fit on-screen.
  useEffect(() => {
    if (paused) return;
    if (posts.length <= perView) return;
    timerRef.current = setInterval(() => {
      setIndex((i) => i + 1);
    }, SLIDE_INTERVAL_MS);
    return () => clearInterval(timerRef.current);
  }, [posts.length, perView, paused]);

  // When the index reaches the cloned segment, let the transition finish
  // then snap silently back to 0. Re-enable the transition on the next
  // frame so React commits the "snap" before the transition turns on again.
  useEffect(() => {
    if (posts.length === 0) return;
    if (index < posts.length) return;

    const snap = setTimeout(() => {
      setAnimating(false);
      setIndex(0);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => setAnimating(true));
      });
    }, SLIDE_TRANSITION_MS);

    return () => clearTimeout(snap);
  }, [index, posts.length]);

  // Reset to a safe index whenever perView changes — avoids the track
  // pointing past the new cloned-segment boundary after a viewport resize.
  useEffect(() => {
    setIndex(0);
  }, [perView]);

  if (!posts.length) return null;

  // Only loop (and clone) when there are genuinely more posts than fit
  // on-screen. Otherwise we'd either duplicate visible posts side-by-side
  // (e.g. 1 post with perView=2 → "p, p") or create confusing near-identical
  // layouts that look like a render bug.
  const canLoop = posts.length > perView;
  const slideWidthPct = 100 / perView;
  const displayList = canLoop ? [...posts, ...posts.slice(0, perView)] : posts;
  const translatePct = canLoop ? -index * slideWidthPct : 0;
  const activeDot = index % posts.length;

  const goTo = (i) => {
    setIndex(i);
    setPaused(true);
    setTimeout(() => setPaused(false), 4000);
  };

  return (
    <section
      id="social"
      className="section-dark"
      style={{
        borderTop: "1px solid var(--line)",
        borderBottom: "1px solid var(--line)",
      }}
    >
      <div className="shell">
        <p className="eyebrow">Social Activity</p>
        <h2 className="section-title" style={{ marginBottom: "1rem" }}>
          From <em>Instagram</em>.
        </h2>
        <p className="lede" style={{ marginBottom: "3rem" }}>
          {" "}
        </p>

        <div
          className="insta-slider-viewport"
          style={{ overflow: "hidden", width: "100%" }}
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
          onTouchStart={() => setPaused(true)}
          onTouchEnd={() => setPaused(false)}
        >
          <div
            className="insta-slider-track"
            style={{
              display: "flex",
              justifyContent: canLoop ? "flex-start" : "center",
              transform: `translateX(${translatePct}%)`,
              transition:
                canLoop && animating
                  ? `transform ${SLIDE_TRANSITION_MS}ms ease`
                  : "none",
              willChange: "transform",
            }}
          >
            {displayList.map((post, i) => (
              <div
                key={`${post._id || "clone"}-${i}`}
                style={{
                  flex: `0 0 ${slideWidthPct}%`,
                  boxSizing: "border-box",
                  padding: "0 12px",
                }}
              >
                <div
                  style={{
                    background: "var(--bg)",
                    border: "1px solid var(--line-strong, var(--line))",
                    borderRadius: 8,
                    overflow: "hidden",
                    height: 480,
                    position: "relative",
                    boxShadow:
                      "0 1px 2px rgba(10,10,10,0.04), 0 12px 32px rgba(10,10,10,0.10)",
                    transition: "transform 220ms ease, box-shadow 220ms ease",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "translateY(-2px)";
                    e.currentTarget.style.boxShadow =
                      "0 2px 4px rgba(10,10,10,0.06), 0 18px 40px rgba(10,10,10,0.14)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow =
                      "0 1px 2px rgba(10,10,10,0.04), 0 12px 32px rgba(10,10,10,0.10)";
                  }}
                >
                  <iframe
                    src={toEmbedUrl(post.url)}
                    title={`Instagram post ${i + 1}`}
                    allowTransparency="true"
                    allowFullScreen
                    frameBorder="0"
                    scrolling="no"
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: "100%",
                      height: 700,
                      border: "none",
                      display: "block",
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Dot navigation */}
        {posts.length > 1 && (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              gap: "8px",
              marginTop: "2rem",
            }}
          >
            {posts.map((_, i) => (
              <button
                key={i}
                onClick={() => goTo(i)}
                aria-label={`Go to slide ${i + 1}`}
                style={{
                  width: i === activeDot ? "24px" : "8px",
                  height: "8px",
                  borderRadius: "100px",
                  border: "none",
                  cursor: "pointer",
                  padding: 0,
                  background:
                    i === activeDot
                      ? "#ffffff"
                      : "rgba(255,255,255,0.3)",
                  transition: "width 300ms ease, background 300ms ease",
                }}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
